import { InfoOutline, TuneRounded } from '@mui/icons-material';
import { useHoldToRepeat } from 'Hooks/useHoldToRepeat';
import PlusButton from 'assets/images/plusButton.svg';
import MinusButton from 'assets/images/minusButton.svg';
import ResetIcon from 'assets/images/Repertoire/Reset.svg';
import { RATE_RANGE } from 'Phaser/Gameplay/Complexity/complexityModel.types';
import { REST_CADENCE_RANGE, clampRate, type ComplexitySettings } from 'Utils/ComplexitySettings';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { CustomSwitch } from './StyledComponents';
import ScrollablePanelBody from './ScrollablePanelBody';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Add, Remove } from '@mui/icons-material';
import { MODE_CONVERSION_OPTIONS, type ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';

const SETTINGS_PANEL_Z_INDEX = 3000;
/** Distance from the panel to the edge of its container. */
const PANEL_LEFT_PX = 10;
/**
 * Fixed so info notes wrap instead of resizing the panel. Wide enough that the
 * longest stepper description still sits on one line — at less than this the
 * Drop Rate row wraps while Climb Rate does not, which reads as a defect.
 */
const PANEL_WIDTH_PX = 430;
/** Where the caret sits until the cog is measured. */
const CARET_FALLBACK_LEFT_PX = 26;
/** How far the panel sits above its container's bottom until the cog is measured. */
const PANEL_FALLBACK_BOTTOM_PX = 60;
/** How far the caret's tip hangs below the panel's bottom edge. */
const CARET_PROTRUSION_PX = 10;
/** Gap left between the caret's tip and the top of the cog. */
const CARET_TO_COG_GAP_PX = 5;
/** Clearance the panel keeps above its container's bottom, i.e. the footer. */
const PANEL_MIN_BOTTOM_PX = 15;
/** Clearance the panel keeps below the header. */
const PANEL_TOP_GAP_PX = 15;
/** The lesson header, which the panel's top is measured against. */
const HEADER_SELECTOR = '.lesson-container-styles';
/** Height the panel falls back to before the header can be measured. */
const PANEL_FALLBACK_MAX_HEIGHT = 'calc(100vh - 160px)';
/** Nearest the caret may sit to either end of the panel's edge. */
const CARET_MIN_LEFT_PX = 20;
/** The settings cog the panel opens from; the caret is aimed at it. */
export const SETTINGS_COG_ELEMENT_ID = 'settings-cog';
const SETTINGS_SELECT_MENU_Z_INDEX = SETTINGS_PANEL_Z_INDEX + 2000;
const MAX_TRANSPOSE_SEMITONES = 7;
const TRANSPOSE_SEMITONE_OPTIONS = Array.from(
  { length: MAX_TRANSPOSE_SEMITONES * 2 + 1 },
  (_, index) => index - MAX_TRANSPOSE_SEMITONES,
);
const getTransposeLabel = (semitoneSteps: number): string => {
  if (semitoneSteps === 0) {
    return 'Concert (0)';
  }
  const absSemitones = Math.abs(semitoneSteps);
  return `${semitoneSteps > 0 ? '+' : ''}${semitoneSteps} ${
    absSemitones === 1 ? 'semitone' : 'semitones'
  }`;
};

const selectSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: '42px',
    color: '#ECECED',
    backgroundColor: '#243056',
    borderRadius: '8px',
    fontFamily: 'Lato',
    fontSize: '14px',
    fontWeight: 600,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 36px 8px 10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#525969',
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiSvgIcon-root': {
    color: '#ECECED',
  },
} as const;

const selectMenuProps = {
  sx: {
    zIndex: SETTINGS_SELECT_MENU_Z_INDEX,
  },
  PaperProps: {
    sx: {
      zIndex: SETTINGS_SELECT_MENU_Z_INDEX + 1,
      mt: '6px',
      color: '#ECECED',
      backgroundColor: '#10192E',
      border: '1px solid #3E4C73',
      borderRadius: '10px',
      boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
      maxHeight: '260px',
      '& .MuiMenuItem-root': {
        minHeight: '36px',
        fontFamily: 'Lato',
        fontSize: '14px',
        fontWeight: 600,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(111, 135, 241, 0.32)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(111, 135, 241, 0.22)',
      },
    },
  },
} as const;

const CustomSlider = ({
  value,
  onChange,
  min,
  max,
  step,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
}) => {
  return (
    <input
      className="custom-slider"
      type="range"
      value={value}
      onChange={(e) => onChange(e.target.valueAsNumber)}
      min={min}
      max={max}
      step={step}
    />
  );
};

const ZoomSlider = ({
  zoomLevel,
  setZoomLevel,
}: {
  zoomLevel: number;
  setZoomLevel: (zoomLevel: number) => void;
}) => {
  const [minZoomLevel, maxZoomLevel] = [0.5, 1];

  return (
    <Box
      className="zoom-slider-container open"
      sx={{
        //position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        top: 10,
        right: 10,
        //width: '200px',
        width: '100%',
        height: '34px',
        padding: '5px',
        zIndex: 1,
        cursor: 'pointer',
      }}
    >
      <Typography>Zoom Level</Typography>
      <Box
        /*className={isOpen ? "inner-container vis" : "inner-container hid"}*/
        sx={{
          flexGrow: 1,
          transition: '0.5s ease-in-out',
          //marginRight: '35px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Remove
          sx={{ color: '#414753' }}
          onClick={() => setZoomLevel(Math.max(minZoomLevel, zoomLevel - 0.05))}
        />
        <CustomSlider
          min={minZoomLevel}
          step={0.05}
          max={maxZoomLevel}
          value={Math.max(minZoomLevel, Math.min(maxZoomLevel, zoomLevel))}
          onChange={(value) => setZoomLevel(value)}
        />
        <Add
          sx={{ color: '#414753' }}
          onClick={() => setZoomLevel(Math.min(maxZoomLevel, zoomLevel + 0.05))}
        />
      </Box>
      {process.env.REACT_APP_NODE_ENV != 'prd' && (
        <p
          style={{
            color: '#000',
            opacity: 0.5,
            position: 'absolute',
            right: 10,
            top: 30,
            fontSize: 12,
          }}
        >
          [debug] Zoom Level: {(zoomLevel ?? 0.9).toFixed(2)}
        </p>
      )}
    </Box>
  );
};

/**
 * A label/description row with a hold-to-repeat stepper, as the tempo panel's
 * custom thresholds use. Values are whole numbers; the caller scales them.
 */
const StepperRow = ({
  label,
  description,
  value: committedValue,
  min,
  max,
  format,
  onChange,
}: {
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}) => {
  const [value, setValue] = useState(committedValue);
  const { incrementHandlers, decrementHandlers } = useHoldToRepeat(value, onChange, {
    min,
    max,
    onLocalChange: setValue,
  });

  useEffect(() => setValue(committedValue), [committedValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginTop: '10px',
      }}
    >
      <Box>
        <Typography sx={{ fontFamily: 'Lato', fontSize: '14px', color: '#ECECED' }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ fontFamily: 'Lato', fontSize: '12px', color: '#6C6C88' }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <Box sx={stepperButtonStyles} {...decrementHandlers}>
          <img src={MinusButton} width={30} height={30} alt={`Decrease ${label}`} />
        </Box>
        <Typography
          sx={{ fontFamily: 'Lato', fontSize: '18px', minWidth: '46px', textAlign: 'center' }}
        >
          {format(value)}
        </Typography>
        <Box sx={stepperButtonStyles} {...incrementHandlers}>
          <img src={PlusButton} width={30} height={30} alt={`Increase ${label}`} />
        </Box>
      </Box>
    </Box>
  );
};

const stepperButtonStyles = {
  borderRadius: 2,
  width: '30px',
  height: '30px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': { cursor: 'pointer', boxShadow: '0px 0px 5px 5px rgb(202,124,98,.5)' },
};

/** The dark rounded explainer the info buttons reveal, as the tempo panel uses. */
const InfoNote = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      backgroundColor: 'rgba(50, 53, 62, 1)',
      padding: '12px 15px',
      marginTop: '10px',
      borderRadius: '8px',
    }}
  >
    <Typography sx={{ textAlign: 'left', fontSize: '14px' }}>{children}</Typography>
  </Box>
);

const InfoButton = ({ shown, onClick }: { shown: boolean; onClick: () => void }) => (
  <InfoOutline
    role="button"
    aria-label="More information"
    onClick={onClick}
    sx={{ color: shown ? '#6F87F1' : 'white', cursor: 'pointer' }}
  />
);

/**
 * The complexity controls (MUS-401): how fast difficulty climbs and drops,
 * whether streaks compound, and how often rest phrases appear.
 *
 * Rates are stepped in whole percentage points, so the stepper works in percent
 * and the fractions the engine wants are derived on commit — stepping a
 * floating-point rate directly accumulates drift.
 */
const ComplexitySettingsSection = ({
  settings,
  onChange,
  onReset,
}: {
  settings: ComplexitySettings;
  onChange: (patch: Partial<ComplexitySettings>) => void;
  onReset: (keys: ReadonlyArray<keyof ComplexitySettings>) => void;
}) => {
  const [sectionInfoShown, setSectionInfoShown] = useState(false);
  const [streakInfoShown, setStreakInfoShown] = useState(false);
  const [restInfoShown, setRestInfoShown] = useState(false);

  const asPercent = (rate: number) => Math.round(rate * 100);
  const rateBounds = {
    min: Math.round(RATE_RANGE.min * 100),
    max: Math.round(RATE_RANGE.max * 100),
  };

  return (
    <Box sx={{ marginTop: '16px', borderTop: '1px solid #414753', paddingTop: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <TuneRounded sx={{ color: '#6F87F1' }} />
        <Typography sx={{ flex: 1, fontSize: '16px' }}>Complexity Settings</Typography>
        <InfoButton
          shown={sectionInfoShown}
          onClick={() => setSectionInfoShown((shown: boolean) => !shown)}
        />
      </Box>
      {sectionInfoShown && (
        <InfoNote>Control how the difficulty adapts to your playing in real time.</InfoNote>
      )}

      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '8px',
          padding: '4px 12px 12px',
          marginTop: '12px',
        }}
      >
        <StepperRow
          label="Climb Rate"
          description="How much to increase complexity."
          value={asPercent(settings.climbRate)}
          min={rateBounds.min}
          max={rateBounds.max}
          format={(percent) => `${percent}%`}
          onChange={(percent) => onChange({ climbRate: clampRate(percent / 100) })}
        />
        <StepperRow
          label="Drop Rate"
          description="How much to decrease complexity."
          value={asPercent(settings.dropRate)}
          min={rateBounds.min}
          max={rateBounds.max}
          format={(percent) => `${percent}%`}
          onChange={(percent) => onChange({ dropRate: clampRate(percent / 100) })}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
          <CustomSwitch
            checked={settings.streakBoost}
            onChange={(_, value) => onChange({ streakBoost: value as boolean })}
          />
          <Typography sx={{ flex: 1 }}>Streak Boost</Typography>
          <InfoButton
            shown={streakInfoShown}
            onClick={() => setStreakInfoShown((shown: boolean) => !shown)}
          />
        </Box>
        {streakInfoShown && <InfoNote>Add a multiplier to your streaks!</InfoNote>}

        <Box
          onClick={() => onReset(['climbRate', 'dropRate', 'streakBoost'])}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '14px',
            '&:hover': { cursor: 'pointer' },
          }}
        >
          <img src={ResetIcon} width={22} height={22} alt="" />
          <Typography sx={{ fontSize: '14px' }}>Reset</Typography>
        </Box>
        <Typography
          sx={{ textAlign: 'center', fontSize: '12px', color: '#6C6C88', marginTop: '8px' }}
        >
          Hold + or – to change faster.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
        <CustomSwitch
          checked={settings.restPhrasesEnabled}
          onChange={(_, value) => onChange({ restPhrasesEnabled: value as boolean })}
        />
        <Typography sx={{ flex: 1 }}>Rest Phrases</Typography>
        {settings.restPhrasesEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RestCadenceStepper
              value={settings.restPhraseCadence}
              onChange={(cadence) => onChange({ restPhraseCadence: cadence })}
            />
          </Box>
        )}
        <InfoButton
          shown={restInfoShown}
          onClick={() => setRestInfoShown((shown: boolean) => !shown)}
        />
      </Box>
      {restInfoShown && (
        <InfoNote>Chose how many game phrases you&rsquo;d like in between a rest phrase.</InfoNote>
      )}
    </Box>
  );
};

/** The rest phrase counter — same stepper, no label column. */
const RestCadenceStepper = ({
  value: committedValue,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) => {
  const [value, setValue] = useState(committedValue);
  const { incrementHandlers, decrementHandlers } = useHoldToRepeat(value, onChange, {
    min: REST_CADENCE_RANGE.min,
    max: REST_CADENCE_RANGE.max,
    onLocalChange: setValue,
  });

  useEffect(() => setValue(committedValue), [committedValue]);

  return (
    <>
      <Box sx={stepperButtonStyles} {...decrementHandlers}>
        <img src={MinusButton} width={26} height={26} alt="Fewer phrases between rests" />
      </Box>
      <Typography sx={{ fontSize: '18px', minWidth: '28px', textAlign: 'center' }}>
        {value}
      </Typography>
      <Box sx={stepperButtonStyles} {...incrementHandlers}>
        <img src={PlusButton} width={26} height={26} alt="More phrases between rests" />
      </Box>
    </>
  );
};

type SettingsPanelProps = {
  open: boolean;
  onClose?: () => void;
  setZoomLevel: (zoomLevel: number) => void;
  setCursorVisible: (visible: boolean) => void;
  setPianoSound: (sound: boolean) => void;
  setMetronomeSound: (sound: boolean) => void;
  pianoSound: boolean;
  cursorVisible: boolean;
  zoomLevel: number;
  metronomeSound: boolean;
  setShowDebugTierSummary: (x: boolean) => void;
  showDebugTierSummary: boolean;
  setShowDebugRepertoireUnlocked: (x: boolean) => void;
  showDebugRepertoireUnlocked: boolean;
  showTransposeControls?: boolean;
  transposeSemitones?: number;
  onTransposeChange?: (nextSemitones: number) => void;
  modeConversion?: ParallelModeConversion;
  onModeConversionChange?: (nextMode: ParallelModeConversion) => void;
  /** Complexity controls. Omitted when complexity adjustment is off. */
  complexitySettings?: ComplexitySettings;
  onComplexitySettingsChange?: (patch: Partial<ComplexitySettings>) => void;
  onComplexitySettingsReset?: (keys: ReadonlyArray<keyof ComplexitySettings>) => void;
};

export default function SettingsPanel({
  onClose,
  zoomLevel,
  setZoomLevel,
  pianoSound,
  setPianoSound,
  metronomeSound,
  setMetronomeSound,
  cursorVisible,
  setCursorVisible,
  setShowDebugTierSummary,
  showDebugTierSummary,
  setShowDebugRepertoireUnlocked,
  showDebugRepertoireUnlocked,
  showTransposeControls = false,
  transposeSemitones = 0,
  onTransposeChange,
  modeConversion = 'off',
  onModeConversionChange,
  complexitySettings,
  onComplexitySettingsChange,
  onComplexitySettingsReset,
}: SettingsPanelProps) {
  const transposeModeConversionEnabled = useFeatureFlag(FeatureFlag.USE_TRANSPOSE_MODE_CONVERSION);
  const panelRef = useRef<HTMLDivElement>(null);
  // Where the caret sits, measured from the panel's left edge. Defaults to the
  // old fixed offset so the panel still looks deliberate before the measure
  // lands, or if the cog is absent (free play hides it).
  const [caretLeft, setCaretLeft] = useState(CARET_FALLBACK_LEFT_PX);
  const [panelBottom, setPanelBottom] = useState(PANEL_FALLBACK_BOTTOM_PX);
  const [panelMaxHeight, setPanelMaxHeight] = useState<string>(PANEL_FALLBACK_MAX_HEIGHT);

  useLayoutEffect(() => {
    const measure = () => {
      const panel = panelRef.current;
      const cog = document.getElementById(SETTINGS_COG_ELEMENT_ID);
      const container = panel?.offsetParent;
      if (!panel || !cog || !container) return;
      const panelRect = panel.getBoundingClientRect();
      const cogRect = cog.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const cogCentre = cogRect.left + cogRect.width / 2 - panelRect.left;
      // Keep it on the panel: a cog far to the right of a narrow panel would
      // otherwise put the caret off the end of the edge it points from.
      setCaretLeft(
        Math.min(Math.max(cogCentre, CARET_MIN_LEFT_PX), panelRect.width - CARET_MIN_LEFT_PX),
      );

      // Sit the panel so the caret's tip stops just short of the cog, rather
      // than at a fixed height that drifts from it. Measured off the container
      // because that is what `bottom` resolves against.
      const caretTipTarget = cogRect.top - CARET_TO_COG_GAP_PX;
      const bottom = Math.max(
        containerRect.bottom - caretTipTarget + CARET_PROTRUSION_PX,
        PANEL_MIN_BOTTOM_PX,
      );
      setPanelBottom(bottom);

      // Cap the height so the panel stops short of the header rather than
      // running up behind it. Measured, because the header is 160px on desktop
      // and 80 on mobile, and slides during playback.
      const header = document.querySelector(HEADER_SELECTOR);
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      const available = containerRect.bottom - bottom - headerBottom - PANEL_TOP_GAP_PX;
      setPanelMaxHeight(`${Math.max(available, 0)}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  const rowStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    color: '#ECECED',
  };
  return (
    <>
      {onClose && (
        <Box
          onClick={onClose}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: SETTINGS_PANEL_Z_INDEX - 1,
          }}
        />
      )}
      <Box
        ref={panelRef}
        sx={{
          position: 'absolute',
          zIndex: SETTINGS_PANEL_Z_INDEX,
          left: `${PANEL_LEFT_PX}px`,
          bottom: `${panelBottom}px`,
          backgroundColor: '#050B13',
          padding: '16px 32px',
          borderRadius: '8px',
          // Fixed, not intrinsic: without this the info notes size the panel,
          // so opening one visibly widens it and closing it snaps back. Now
          // their text wraps within the width the rest of the panel sets.
          width: `${PANEL_WIDTH_PX}px`,
          maxWidth: 'calc(100vw - 20px)',
          boxSizing: 'border-box',
          maxHeight: panelMaxHeight,
          // A column so the heading keeps its size and the body takes the rest.
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Points at the settings cog, so the panel reads as having come out of
            it. The cog sits at different offsets on mobile and desktop, so its
            position is measured rather than assumed; `caretLeft` falls back to
            the old fixed offset if the cog is not on screen. */}
        <Box
          sx={{
            position: 'absolute',
            bottom: `${-CARET_PROTRUSION_PX}px`,
            left: `${caretLeft}px`,
            transform: 'translateX(-50%)',
            width: '0',
            height: '0',
            borderLeft: '16px solid transparent',
            borderRight: '16px solid transparent',
            borderTop: '16px solid #050B13', // Matches the panel background
          }}
        />
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            textTransform: 'uppercase',
            marginBottom: '16px',
            paddingBottom: '10px',
            borderBottom: '1px solid #414753',
            // Keeps its height while the body below it takes the remainder.
            flexShrink: 0,
          }}
        >
          Settings
        </Typography>
        {/* The heading stays put; everything below it scrolls. Capped against
            the viewport so the panel can never grow past the screen — the
            complexity section alone can outrun a short window. */}
        <ScrollablePanelBody>
          <Box>
            <Box sx={{ ...rowStyles, flexDirection: 'column', marginBottom: '10px' }}>
              <ZoomSlider zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
            </Box>
          </Box>
          <Box>
            <Box sx={rowStyles}>
              <CustomSwitch
                checked={pianoSound}
                onChange={(_, value) => setPianoSound(value as boolean)}
              />
              <Typography variant="body1">Keyboard Sfx</Typography>
            </Box>
          </Box>
          <Box sx={rowStyles}>
            <CustomSwitch
              checked={metronomeSound}
              onChange={(_, value) => setMetronomeSound(value as boolean)}
            />
            <Typography variant="body1">Metronome Sound</Typography>
          </Box>
          {showTransposeControls && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
              <Box>
                <Typography variant="body1" sx={{ marginBottom: '6px' }}>
                  Transpose
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={`${transposeSemitones}`}
                  onChange={(event) => onTransposeChange?.(Number(event.target.value))}
                  SelectProps={{ MenuProps: selectMenuProps }}
                  sx={selectSx}
                >
                  {TRANSPOSE_SEMITONE_OPTIONS.map((step) => (
                    <MenuItem key={step} value={`${step}`}>
                      {getTransposeLabel(step)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              {transposeModeConversionEnabled && (
                <Box>
                  <Typography variant="body1" sx={{ marginBottom: '6px' }}>
                    Scale / Mode
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={modeConversion}
                    onChange={(event) =>
                      onModeConversionChange?.(event.target.value as ParallelModeConversion)
                    }
                    SelectProps={{ MenuProps: selectMenuProps }}
                    sx={selectSx}
                  >
                    {MODE_CONVERSION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
            </Box>
          )}
          <Box sx={rowStyles}>
            <CustomSwitch
              checked={cursorVisible}
              onChange={(_, value) => setCursorVisible(value as boolean)}
            />
            <Typography variant="body1">Cursor</Typography>
          </Box>
          {complexitySettings && onComplexitySettingsChange && onComplexitySettingsReset && (
            <ComplexitySettingsSection
              settings={complexitySettings}
              onChange={onComplexitySettingsChange}
              onReset={onComplexitySettingsReset}
            />
          )}
          {process.env.REACT_APP_NODE_ENV !== 'prd' && (
            <Box sx={rowStyles}>
              <CustomSwitch
                checked={showDebugTierSummary}
                onChange={(_, value) => setShowDebugTierSummary(value as boolean)}
              />
              <Typography variant="body1">Debug Tier Summary</Typography>
            </Box>
          )}
          {process.env.REACT_APP_NODE_ENV !== 'prd' && (
            <Box sx={rowStyles}>
              <CustomSwitch
                checked={showDebugRepertoireUnlocked}
                onChange={(_, value) => setShowDebugRepertoireUnlocked(value as boolean)}
              />
              <Typography variant="body1">Debug Repertoire Unlocked</Typography>
            </Box>
          )}
        </ScrollablePanelBody>
      </Box>
    </>
  );
}
